import { Navigate, useParams, Link } from 'react-router-dom';
import { PageHeader } from '@/components/layout/PageHeader';
import { getSetupGuide } from './config/platformSetupGuides';
import { Button } from '@/components/ui/Button';

export function PlatformGuidePage() {
  const { platformId } = useParams<{ platformId: string }>();
  const guide = platformId ? getSetupGuide(platformId) : undefined;

  if (!guide) return <Navigate to="/social/connect?tab=guides" replace />;

  const beforeYouStart = guide.beforeYouStart ?? [
    'Have admin access to your social business accounts.',
    'Allow 15–20 minutes for first-time setup.',
    'Keep your phone nearby for verification codes if the network asks.',
  ];

  const validationSteps = guide.validationSteps ?? [
    'Open Connection Center → Diagnostics and run all tests.',
    'Confirm health score is above 90%.',
    'Send a test message or comment and reply from the ERP.',
  ];

  const reconnectSteps = guide.reconnectSteps ?? [
    'Go to Connection Center → select the channel → Refresh token.',
    'If that fails, open the setup wizard and connect again.',
    'Check alert emails if your organization enabled them.',
  ];

  const healthSteps = guide.healthMonitoring ?? [
    'Review Connection Center daily for token and webhook status.',
    'Fix red alerts before they affect customers.',
    'Use My Work queue for connection issues assigned to you.',
  ];

  return (
    <div className="page-content">
      <PageHeader
        title={`${guide.label} setup guide`}
        description="Written for business owners and marketing teams — no technical background required"
        breadcrumbs={[
          { label: 'Social', to: '/social' },
          { label: 'Connection Center', to: '/social/connect' },
          { label: guide.label },
        ]}
        actions={
          <Link to={`/social/connect/wizard/${guide.platformId}`}>
            <Button>Start guided setup</Button>
          </Link>
        }
      />

      <div className="grid gap-6 lg:grid-cols-3">
        <section className="card p-5 lg:col-span-2">
          <h2 className="text-sm font-semibold">Overview</h2>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">{guide.overview}</p>

          <h2 className="mt-6 text-sm font-semibold">Requirements</h2>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm">
            {guide.requirements.map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ul>

          <h2 className="mt-6 text-sm font-semibold">Before you start</h2>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-slate-600 dark:text-slate-400">
            {beforeYouStart.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ul>

          {guide.unsupportedFeatures.length > 0 && (
            <>
              <h2 className="mt-6 text-sm font-semibold">Not available on this network</h2>
              <p className="mt-1 text-sm text-amber-800 dark:text-amber-300">
                {guide.unsupportedFeatures.join(' · ')}
              </p>
            </>
          )}

          <h2 className="mt-6 text-sm font-semibold">Step-by-step instructions</h2>
          <ol className="mt-3 space-y-4">
            {guide.steps.map((step, idx) => (
              <li key={`${step.id}-${idx}`} className="rounded-lg border p-4 dark:border-slate-700">
                <p className="font-medium text-slate-900 dark:text-slate-100">
                  {idx + 1}. {step.title}
                </p>
                <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">{step.description}</p>
                <ul className="mt-2 list-disc pl-5 text-sm">
                  {step.checklist.map((c) => (
                    <li key={c}>{c}</li>
                  ))}
                </ul>
                {step.optional && (
                  <p className="mt-2 text-xs text-slate-500">Optional step</p>
                )}
              </li>
            ))}
          </ol>

          <h2 className="mt-6 text-sm font-semibold">Connection validation</h2>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm">
            {validationSteps.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ul>
        </section>

        <aside className="space-y-4">
          <section className="card p-5">
            <h2 className="text-sm font-semibold">Reconnect process</h2>
            <ol className="mt-2 list-decimal space-y-2 pl-5 text-sm text-slate-600 dark:text-slate-400">
              {reconnectSteps.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ol>
          </section>

          <section className="card p-5">
            <h2 className="text-sm font-semibold">Health monitoring</h2>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-slate-600 dark:text-slate-400">
              {healthSteps.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ul>
            <Link to="/social/connect?tab=health" className="mt-3 inline-block text-sm text-primary-600 hover:underline">
              Open health dashboard →
            </Link>
          </section>

          <section className="card p-5">
            <h2 className="text-sm font-semibold">Common questions</h2>
            <dl className="mt-2 space-y-3 text-sm">
              {guide.faq.map((f) => (
                <div key={f.q}>
                  <dt className="font-medium">{f.q}</dt>
                  <dd className="text-slate-600 dark:text-slate-400">{f.a}</dd>
                </div>
              ))}
            </dl>
          </section>

          <section className="card p-5">
            <h2 className="text-sm font-semibold">Troubleshooting</h2>
            <ul className="mt-2 space-y-3 text-sm">
              {guide.commonErrors.map((e) => (
                <li key={e.error} className="rounded border p-3 dark:border-slate-700">
                  <p className="font-medium text-red-700 dark:text-red-300">{e.error}</p>
                  <p className="mt-1 text-slate-500">
                    <span className="font-medium">Why:</span> {e.cause}
                  </p>
                  <p className="mt-1">
                    <span className="font-medium">Fix:</span> {e.fix}
                  </p>
                </li>
              ))}
            </ul>
          </section>

          {guide.developerPortalUrl && (
            <p className="text-xs text-slate-500">
              Advanced reference:{' '}
              <a href={guide.developerPortalUrl} target="_blank" rel="noreferrer" className="text-link">
                partner documentation
              </a>{' '}
              (for your IT team only).
            </p>
          )}
        </aside>
      </div>
    </div>
  );
}
